import { useState, useEffect, useRef, useMemo } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import axios from "axios";
import { origin } from "../../../Config/Api-constants";
import { Base64 } from "js-base64";
import { Checkbox, Grid, SvgIcon, Autocomplete, Chip, Box } from "@mui/material";
import { useSelector } from "react-redux";
import "../../../../Assets/Styles/Containers/_button.scss";
import "../../../../Assets/Styles/Containers/_inspection.scss";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { arrowDown } from "../../../../Assets/Svg/Svg";
import CommentRating from "../Rating/CommentRating";
import Location from "../Location/Location";
import FroalaEditorComponent from "../../../Common/HtmlEditor/FroalaEditorComponent";
import { getTextColor } from "../../../Helpers/HelperFunctions";
import {
  getTagChipVisibility,
  mergeMuiOutlinedInputRef,
  useTagFieldCollapsedVisibleCount,
} from "./tagAutocompleteFieldUtils";

const froalaConfig = {
  imagePaste: false,
  spellCheck: true,
  heightMin: 145,
  heightMax: 201,
  linkAutoPrefix: 'https://',
  linkAlwaysBlank: true,
  toolbarButtons: {
    moreText: {
      buttons: ['bold', 'italic', 'underline', '|', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor'],
      align: 'left',
      buttonsVisible: 7
    },
    moreParagraph: {
      buttons: ['|', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL'],
      align: 'left',
      buttonsVisible: 8
    },
    moreRich: {
      buttons: ['|', 'insertLink', 'outdent', 'indent'],
      align: 'left',
      buttonsVisible: 1
    },
    moreMisc: {
      buttons: ['aiHelper', 'undo', 'redo', 'spellChecker', 'selectAll', 'html', 'clearFormatting'], // 👈 aiHelper first
      align: 'right',
      buttonsVisible: 3  // 👈 Adjust this if you want aiHelper always visible
    }
  },
  toolbarButtonsXS: [['fontFamily', 'fontSize', 'bold', 'italic', 'underline', '|'], ['undo', 'redo']],
  pluginsEnabled: [
    'wordPaste',
    'spellChecker',
    'codeBeautifier',
    'fontFamily',
    'fontSize',
    'colors',
    'align',
    'lists',
    'paragraphFormat',
    'link'
  ],
};

const toolbarButtonsConfig = {
  moreText: {
    buttons: ["bold", "italic", "underline", "fontFamily", "fontSize", "textColor", "backgroundColor", "alignLeft", "alignCenter", "alignRight"],
    align: "left",
    buttonsVisible: 3,
  },
  moreRich: {
    buttons: ["insertLink", "emoticons", "specialCharacters", "fontAwesome", "formatOLSimple", "formatUL", "outdent", "indent"],
    align: "left",
    buttonsVisible: 0,
  },
  moreMisc: { buttons: ["undo", "redo"], align: "right", buttonsVisible: 2 },
};

const AddCommentModal = ({
  editComment,
  templateId,
  categoryId,
  lineUuid,
  recommendation,
  commentUuid,
  lineName,
  categoryName,
  comment_dropdowns,
  comment_input_controls,
  commentIndex,
  ratingValue,
  setRatingValue,
  locationValue,
  setLocationValue,
  tag,
  setTag,
  commentTitle,
  setCommentTitle,
  setEditorContent,
  editorContent,
  selectedFilterTagID,
  prefetchedTagList,
}) => {
  const storeData = useSelector((state) => state.CommonReducer);
  const editorRef = useRef();
  const initialTagPrefillDoneRef = useRef(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagsInputFocused, setTagsInputFocused] = useState(false);
  const [tagList, setTagList] = useState(Array.isArray(prefetchedTagList) ? prefetchedTagList : []);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const isTexasTemplate = storeData?.inspectionData?.data?.inspectiontemplates?.find((template) => template.template_id === templateId)?.is_texas;

  useEffect(() => {
    if (Array.isArray(prefetchedTagList) && prefetchedTagList.length > 0) {
      setTagList(prefetchedTagList);
    }
  }, [prefetchedTagList]);

  useEffect(() => {
    if (Array.isArray(prefetchedTagList) && prefetchedTagList.length > 0) return;
    const getTagList = async () => {
      let baseUrl = await origin();
      let sessionAPIKey = sessionStorage.getItem("CK");
      let sessionSecretKey = sessionStorage.getItem("SK");
      axios.get(`${baseUrl}tags`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
          "api-key": `${Base64.decode(sessionAPIKey)}`,
          platform: "web",
          "api-platform": "WRW",
        },
      }).then(res => {
        setTagList(res.data.data || [])
      }).catch(err => console.log('err', err))
    }
    getTagList();
  }, [prefetchedTagList])

  useEffect(() => {
    if (editComment) return;
    if (initialTagPrefillDoneRef.current) return;
    if (!Array.isArray(selectedFilterTagID) || selectedFilterTagID.length === 0) return;
    if (Array.isArray(tag) && tag.length > 0) return;

    // Use already loaded CL tags first for immediate UI feedback;
    // fall back to fetched tags list when available.
    const fastSourceTags = Array.isArray(storeData?.clTagNN) ? storeData.clTagNN : [];
    const availableTags = (tagList && tagList.length > 0) ? tagList : fastSourceTags;
    if (!Array.isArray(availableTags) || availableTags.length === 0) return;

    const selectedFilterIds = selectedFilterTagID.map((id) => String(id));
    const defaultSelectedTags = availableTags.filter((item) => selectedFilterIds.includes(String(item?.id)));

    if (defaultSelectedTags.length > 0) {
      setTag(defaultSelectedTags);
      initialTagPrefillDoneRef.current = true;
    }
  }, [editComment, selectedFilterTagID, tagList, tag, setTag, storeData?.clTagNN]);

  useEffect(() => {
    const timer = setTimeout(() => {
      document.getElementById("commentTitle")?.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (name, content) => {
    setEditorContent(prevState => ({ ...prevState, innerHTML: content?.replace(/<([a-zA-Z0-9]+)>\s*<\/\1>\s*/g, "") }));
  };

  const handleTagOpen = (e) => {
    e.stopPropagation();
    setTagOpen(true);
  };

  const handleTagClose = (e) => {
    e.stopPropagation();
    setTagOpen(false);
  };

  const tagAutocompleteValue = useMemo(
    () =>
      (tagList || []).filter((tagItem) =>
        (tag || []).some((selectedTag) => String(selectedTag?.id) === String(tagItem?.id))
      ),
    [tagList, tag]
  );

  const { tagsInputRootRef, collapsedVisibleTagCount } = useTagFieldCollapsedVisibleCount({
    tagOpen,
    tagsInputFocused,
    selectedTags: tagAutocompleteValue,
  });

  return (
    <Grid container>
      <Grid item container xs={7.5} pr={1}>
        <Grid item xs={12} mt={2}>
          <FormControl variant="standard" className="forms-control">
            <label className="non-narrative-comment-label" htmlFor="commentTitle">
              Title
            </label>
            <TextField
              id="commentTitle"
              size="small"
              variant="outlined"
              placeholder="Comment title"
              value={commentTitle}
              onChange={(e) => setCommentTitle(e.target.value)}
              type="text"
            />
          </FormControl>
        </Grid>
        {<Grid item xs={12} container mb={2}>
          <CommentRating
            data={comment_input_controls?.find((data) => data.type === "Rating")}
            title="Rating"
            action="comment"
            templateId={templateId}
            categoryId={categoryId}
            lineUuid={lineUuid}
            commentUuid={commentUuid}
            commentIndex={commentIndex}
            component={"commentModal"}
            editComment={editComment}
            ratingValue={ratingValue}
            setRatingValue={setRatingValue}
          />
        </Grid>}
      </Grid>
      {<Grid item container xs={4.5} alignItems="flex-start" style={{ alignSelf: 'flex-start' }}>
        <Grid item xs={12} mt={2}>
          <FormControl variant="standard" className="forms-control">
            <label className="non-narrative-comment-label" htmlFor="tags">
              TAGS
            </label>
            <Autocomplete
              className="forms-control-tag custom-color autocompleteCustom"
              id="tags-outlined"
              multiple
              size="small"
              options={tagList || []}
              value={tagAutocompleteValue}
              onClick={(e) => e.stopPropagation()}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id != null && value?.id != null && String(option.id) === String(value.id)}
              open={tagOpen}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ccc",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ccc",
                  },
                },
              }}
              onChange={(e, value) => setTag(value)}
              renderTags={(value, getTagProps) => {
                const { visible, hiddenCount } = getTagChipVisibility(value, {
                  tagOpen,
                  tagsInputFocused,
                  collapsedVisibleTagCount,
                });
                return (
                  <>
                    {visible.map((option, index) => (
                      <Chip
                        variant="outlined"
                        size="small"
                        deleteIcon={<CloseIcon sx={{ color: `${getTextColor(option?.color)} !important`, fontSize: 16, backgroundColor: "transparent !important" }} />}
                        label={option?.name}
                        {...getTagProps({ index })}
                        sx={{
                          borderRadius: "35px",
                          border: "1px solid #CED2DA !important",
                          backgroundColor: `${option?.color} !important`,
                          color: `${getTextColor(option?.color)} !important`,
                          maxWidth: 120,
                          paddingLeft: "10px",
                        }}
                      />
                    ))}
                    {hiddenCount > 0 && (
                      <Box component="span" sx={{ ml: 0.5, fontSize: "14px", lineHeight: "24px", color: "#333333" }}>
                        {`+${hiddenCount}`}
                      </Box>
                    )}
                  </>
                );
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                  <Box
                    sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                    style={{
                      marginRight: 8,
                    }}
                  />
                  {option?.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={tag?.length === 0 ? "Add tags" : ""}
                  onClick={!tagOpen ? handleTagOpen : handleTagClose}
                  onFocus={(e) => {
                    params.inputProps?.onFocus?.(e);
                    setTagsInputFocused(true);
                  }}
                  onBlur={(e) => {
                    params.inputProps?.onBlur?.(e);
                    setTagsInputFocused(false);
                    handleTagClose(e);
                  }}
                  InputProps={{
                    ...params.InputProps,
                    ref: mergeMuiOutlinedInputRef(tagsInputRootRef, params.InputProps.ref),
                    style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: tag?.length !== 0 ? "0px" : "20px" },
                    endAdornment: (
                      <>
                        <SearchIcon className={`customSearch ${tag?.length !== 0 ? "hidden" : ""}`} style={{ color: "#9b9b9b", marginRight: "8px" }} />
                        <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={!tagOpen ? handleTagOpen : handleTagClose}>
                          {arrowDown}
                        </SvgIcon>
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid container item xs={12} mt={-1.3}>
          <Location
            data={comment_input_controls?.find((data) => data.type === "Location")}
            title="Location"
            action="comment"
            templateId={templateId}
            categoryId={categoryId}
            lineUuid={lineUuid}
            type="comment"
            commentUuid={commentUuid}
            commentIndex={commentIndex}
            component={"commentModal"}
            editComment={editComment}
            locationValue={locationValue}
            setLocationValue={setLocationValue}
          />
        </Grid>
        <Grid container item xs={12}>
        </Grid>
      </Grid>}
      <Grid container className="input-controll">
        <Grid item xs={12} sm={12}>
          <FormControl variant="standard" className="forms-control">
            <label id="non-narrative-comment" className="non-narrative-comment-label" htmlFor="Comment">
              Comment
            </label>
            <FroalaEditorComponent
              showAI={storeData?.inspectionData?.data?.is_comment_helper_enable}
              commentData={{ comment_text: recommendation, comment_dropdowns: comment_dropdowns }}
              editorData={editorContent}
              editor={editorRef}
              id={"non-narrative-comment"}
              onChange={handleChange}
              froalaConfig={froalaConfig}
              toolbarButtonsConfig={toolbarButtonsConfig}
              showAIModal={showAIModal}
              setShowAIModal={setShowAIModal}
              lineName={lineName}
              categoryName={categoryName}
              commentTitle={commentTitle}
              setEditorContent={setEditorContent}
              isTexasTemplate={isTexasTemplate}
              filterTagList={tagList}
              categoryId={categoryId}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AddCommentModal;
